import * as React from "react";
import { useQuery } from "react-query";
import { ClientConfigsService, UserAccountsService } from "../../../gen/clients/llts";
import { LoadingPage } from "../../../components/LoadingPage/LoadingPage";
import { ErrorPage } from "../../../components/ErrorPage/ErrorPage";
import { OrganizationSelector } from "./components/OrganizationSelector";
import { SignedInUserForm } from "./components/SignedInUserForm";
import Container from "@mui/material/Container";
import { AccessibleAlert } from "components/AccessibleAlert/AccessibleAlert";
import { useTranslation } from "react-i18next";
import { useUserSession } from "../../../hooks/useUserSession";
import { SelectOption } from "@mui/base";
import { SELECT_OPTION_COMPARATOR } from "../../../utils/stringUtils";
import { useNavigation } from "../../../hooks/useNavigation";
import { ROUTES } from "../../../utils/navigationUtils";

interface Props {
  email: string;
}

/**
 * This component is responsible for rendering the create project page for a signed-in user.
 */
const SignedInUserPage: React.FC<Props> = ({ email }) => {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const { externalGroups } = useUserSession();
  const [selectedClientId, setSelectedClientId] = React.useState<string>();

  // Fetch the user account.
  const {
    data: userAccount,
    isLoading: isUserAccountLoading,
    error: userAccountError
  } = useQuery(["getUserAccount", email], {
    queryFn: UserAccountsService.getUserAccount
  });

  // Fetch the linked clients for the user.
  const {
    data: linkedClients,
    isLoading: areLinkedClientsLoading,
    error: linkedClientsError
  } = useQuery(["listLinkedClients", email], {
    queryFn: UserAccountsService.listLinkedClients
  });

  const emailDomain = email.substring(email.indexOf("@") + 1);
  const {
    data: externalGroupsClientConfigs,
    isLoading: areExternalGroupsClientConfigsLoading,
    error: externalGroupsClientConfigsError
  } = useQuery(["listClientConfigs", emailDomain, externalGroups], {
    enabled: !!externalGroups,
    queryFn: () =>
      ClientConfigsService.listClientConfigs({
        emailDomain,
        externalGroups: (externalGroups || []).join(",")
      })
  });

  // Fetch the selected client config.
  const {
    data: selectedClientConfig,
    isLoading: isClientConfigLoading,
    error: clientConfigError
  } = useQuery(["getClientConfig", selectedClientId], {
    enabled: !!selectedClientId,
    queryFn: () => ClientConfigsService.getClientConfig({ clientId: selectedClientId || "" })
  });

  const activeLinkedClients = React.useMemo(() => linkedClients?.filter(c => c.status === "ACTIVE"), [linkedClients]);

  const isLoading =
    isUserAccountLoading || areLinkedClientsLoading || isClientConfigLoading || areExternalGroupsClientConfigsLoading;
  const error = userAccountError || linkedClientsError || clientConfigError || externalGroupsClientConfigsError;

  if (error) {
    return <ErrorPage apiError={error} />;
  }

  if (isLoading || !userAccount || !activeLinkedClients) {
    return <LoadingPage />;
  }

  /**
   * Check if the user's client is active.
   */
  if (userAccount?.clientConfig?.status !== "ACTIVE") {
    return (
      <Container maxWidth="md">
        <AccessibleAlert severity="error" sx={{ mt: 3 }}>
          <span dangerouslySetInnerHTML={{ __html: t("createProject.signedInUser.inactiveClient") }} />
        </AccessibleAlert>
      </Container>
    );
  }

  // Check if the user has active linked clients.
  if (activeLinkedClients.length > 0) {
    // If a client is selected, render the signed-in user form.
    if (selectedClientConfig) {
      return <SignedInUserForm email={email} clientConfig={selectedClientConfig} />;
    } else {
      // If no client is selected, render the organization selector.
      const organizationOptions: SelectOption<string>[] = [
        { value: userAccount.clientConfig?.clientId || "", label: userAccount.clientConfig?.companyName || "" },
        ...activeLinkedClients.map(c => ({ value: c.clientId, label: c.clientName }))
      ].sort(SELECT_OPTION_COMPARATOR);
      return (
        <OrganizationSelector
          userAccount={userAccount}
          organizationOptions={organizationOptions}
          onSelect={setSelectedClientId}
          onCancel={() => navigateTo(ROUTES.HOME)}
        />
      );
    }
  }

  // Render the signed-in user form or an organization selector based on external group client configurations.
  if (externalGroupsClientConfigs && externalGroupsClientConfigs.length > 0) {
    const activeExternalGroupsClientConfigs = externalGroupsClientConfigs.filter(c => c.status === "ACTIVE");
    if (selectedClientConfig) {
      return <SignedInUserForm email={email} clientConfig={selectedClientConfig} />;
    } else if (activeExternalGroupsClientConfigs.length > 0) {
      if (activeExternalGroupsClientConfigs.length === 1) {
        return <SignedInUserForm email={email} clientConfig={externalGroupsClientConfigs[0]} />;
      }
      const organizationOptions: SelectOption<string>[] = activeExternalGroupsClientConfigs
        .map(c => ({
          label: c.companyName,
          value: c.clientId
        }))
        .sort(SELECT_OPTION_COMPARATOR);
      return (
        <OrganizationSelector
          userAccount={userAccount}
          organizationOptions={organizationOptions}
          onSelect={setSelectedClientId}
          onCancel={() => navigateTo(ROUTES.HOME)}
        />
      );
    }
  }

  // Render the signed-in user form directly if the user does not have active linked clients.
  return <SignedInUserForm email={email} clientConfig={userAccount.clientConfig} />;
};

export { SignedInUserPage };
